import React, { useState, useEffect } from 'react';
import { Plus, Search, Target, Pencil, Trash2, RotateCcw, TrendingUp, ArrowRight, FileBadge } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { useToast } from '../../../hooks/use-toast';
import BackButton from '../../../components/admin/BackButton';
import CreateLeadModal from '../../../components/admin/crm/CreateLeadModal';
import EditLeadModal from '../../../components/admin/crm/EditLeadModal';
import PromoteCounterpartyModal from '../../../components/admin/PromoteCounterpartyModal';
import AlsoBadge from '../../../components/admin/AlsoBadge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LEAD_STATUS_COLORS = {
  NEW: 'bg-gray-100 text-gray-800',
  CONTACTED: 'bg-blue-100 text-blue-800',
  QUALIFIED: 'bg-green-100 text-green-800',
  PROPOSAL: 'bg-yellow-100 text-yellow-800',
  NEGOTIATION: 'bg-orange-100 text-orange-800',
  WON: 'bg-green-500 text-white',
  LOST: 'bg-red-100 text-red-800'
};

const Leads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [promoteSource, setPromoteSource] = useState(null);
  const [pagination, setPagination] = useState({ skip: 0, limit: 20 });
  const [totalCount, setTotalCount] = useState(0);
  const { toast } = useToast();

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    fetchLeads();
    fetchCount();
  }, [pagination, searchTerm, statusFilter]);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const searchParam = searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : '';
      const statusParam = statusFilter ? `&status=${statusFilter}` : '';
      const response = await fetch(
        `${API_URL}/api/crm/leads?skip=${pagination.skip}&limit=${pagination.limit}${searchParam}${statusParam}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch leads');

      const data = await response.json();
      setLeads(data);
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast({
        title: 'Error',
        description: 'Failed to load leads',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCount = async () => {
    try {
      const searchParam = searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : '';
      const response = await fetch(
        `${API_URL}/api/crm/leads/count${searchParam}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch count');

      const data = await response.json();
      setTotalCount(data.count);
    } catch (error) {
      console.error('Error fetching count:', error);
    }
  };

  const handleDelete = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;

    try {
      const response = await fetch(`${API_URL}/api/crm/leads/${leadId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to delete lead');

      toast({
        title: 'Success',
        description: 'Lead deleted successfully'
      });
      fetchLeads();
      fetchCount();
    } catch (error) {
      console.error('Error deleting lead:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete lead',
        variant: 'destructive'
      });
    }
  };

  const handleRestore = async (leadId) => {
    try {
      const response = await fetch(`${API_URL}/api/crm/leads/${leadId}/restore`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to restore lead');

      toast({
        title: 'Success',
        description: 'Lead restored successfully'
      });
      fetchLeads();
    } catch (error) {
      console.error('Error restoring lead:', error);
      toast({
        title: 'Error',
        description: 'Failed to restore lead',
        variant: 'destructive'
      });
    }
  };

  const handleEdit = (lead) => {
    setSelectedLead(lead);
    setIsEditModalOpen(true);
  };

  const handlePromoteToOnboarding = async (leadId) => {
    try {
      const r = await fetch(
        `${API_URL}/api/crm/leads/${leadId}/promote-to-onboarding`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
      );
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed');
      }
      const j = await r.json();
      toast({
        title: 'Promoted to onboarding',
        description: `Submission ${j.submission_number}. KYC portal email sent.`,
      });
      fetchLeads();
    } catch (e) {
      toast({
        title: 'Failed to promote',
        description: e.message,
        variant: 'destructive',
      });
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-gray-600';
  };

  const handleNextPage = () => {
    if (pagination.skip + pagination.limit < totalCount) {
      setPagination({ ...pagination, skip: pagination.skip + pagination.limit });
    }
  };

  const handlePrevPage = () => {
    if (pagination.skip > 0) {
      setPagination({ ...pagination, skip: Math.max(0, pagination.skip - pagination.limit) });
    }
  };

  const currentPage = Math.floor(pagination.skip / pagination.limit) + 1;
  const totalPages = Math.ceil(totalCount / pagination.limit);

  return (
    <div>
      <BackButton />

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-[#00B4D8] bg-opacity-10 rounded-lg">
              <Target className="h-6 w-6 text-[#00B4D8]" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-[#2B2D42]">Leads</h2>
              <p className="text-sm text-[#6C757D]">Manage your sales pipeline with lead scoring</p>
            </div>
          </div>
          <Button
            onClick={() => setIsCreateModalOpen(true)}
            className="bg-[#00B4D8] hover:bg-[#0096B8]"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Lead
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#6C757D]" />
              <input
                type="text"
                placeholder="Search leads by company name, contact person, or email..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPagination({ ...pagination, skip: 0 });
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPagination({ ...pagination, skip: 0 });
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
            >
              <option value="">All Status</option>
              <option value="NEW">New</option>
              <option value="CONTACTED">Contacted</option>
              <option value="QUALIFIED">Qualified</option>
              <option value="PROPOSAL">Proposal</option>
              <option value="NEGOTIATION">Negotiation</option>
              <option value="WON">Won</option>
              <option value="LOST">Lost</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-[#6C757D] mb-1">Total Leads</p>
              <p className="text-3xl font-bold text-[#2B2D42]">{totalCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-[#6C757D] mb-1">Current Page</p>
              <p className="text-3xl font-bold text-[#2B2D42]">
                {currentPage} / {totalPages || 1}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-[#6C757D] mb-1">Showing</p>
              <p className="text-3xl font-bold text-[#2B2D42]">{leads.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-[#6C757D] mb-1">Avg Score</p>
              <p className="text-3xl font-bold text-[#2B2D42]">
                {leads.length > 0
                  ? Math.round(leads.reduce((sum, l) => sum + l.lead_score, 0) / leads.length)
                  : 0}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Leads List */}
      {loading ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-[#6C757D]">Loading leads...</p>
          </CardContent>
        </Card>
      ) : leads.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-[#6C757D]">
              {searchTerm || statusFilter ? 'No leads found matching your filters.' : 'No leads yet. Add your first lead!'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {leads.map((lead) => (
            <Card key={lead.id} className={lead.is_deleted ? 'opacity-50' : ''}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-[#2B2D42]">
                        {lead.company_name}
                      </h3>
                      <span className={`px-2 py-1 text-xs rounded-full ${LEAD_STATUS_COLORS[lead.status]}`}>
                        {lead.status}
                      </span>
                      {lead.is_deleted && (
                        <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">
                          Deleted
                        </span>
                      )}
                      <AlsoBadge module="leads" recordId={lead.id} current="lead" />
                      <div className="flex items-center space-x-1">
                        <TrendingUp className={`h-4 w-4 ${getScoreColor(lead.lead_score)}`} />
                        <span className={`text-sm font-semibold ${getScoreColor(lead.lead_score)}`}>
                          Score: {lead.lead_score}
                        </span>
                      </div>
                      {/* P2 CRM 2.0 — Lifecycle status badges */}
                      <div className="flex flex-wrap gap-1" data-testid={`lead-${lead.id}-lifecycle`}>
                        {lead.role && (
                          <span className="px-2 py-0.5 text-[10px] uppercase rounded bg-slate-100 text-slate-700 border border-slate-200">
                            {lead.role}
                          </span>
                        )}
                        {lead.country && (
                          <span className="px-2 py-0.5 text-[10px] rounded bg-slate-50 text-slate-600 border border-slate-200">
                            {lead.country}
                          </span>
                        )}
                        {lead.risk_country_flag && (
                          <span className="px-2 py-0.5 text-[10px] rounded bg-orange-100 text-orange-800 border border-orange-200"
                            title="High-risk jurisdiction">
                            ⚠ High-risk
                          </span>
                        )}
                        {lead.pep_screened && (
                          <span className={`px-2 py-0.5 text-[10px] rounded border ${
                            lead.pep_match
                              ? 'bg-rose-100 text-rose-800 border-rose-200'
                              : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          }`} title="PEP screening result">
                            {lead.pep_match ? 'PEP HIT' : 'PEP clear'}
                          </span>
                        )}
                        {lead.edd_case_id && (
                          <span className="px-2 py-0.5 text-[10px] rounded bg-amber-100 text-amber-800 border border-amber-200"
                            title="EDD case open">
                            EDD
                          </span>
                        )}
                        {lead.kyc_status && (
                          <span className={`px-2 py-0.5 text-[10px] rounded border ${
                            lead.kyc_status === 'completed'
                              ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                              : 'bg-slate-100 text-slate-700 border-slate-200'
                          }`}>
                            KYC: {lead.kyc_status}
                          </span>
                        )}
                        {lead.procurement_supplier_id && (
                          <span className="px-2 py-0.5 text-[10px] rounded bg-indigo-100 text-indigo-800 border border-indigo-200"
                            title="Registered as procurement supplier">
                            Supplier ✓
                          </span>
                        )}
                        {lead.onboarding_submission_id && (
                          <span className="px-2 py-0.5 text-[10px] rounded bg-purple-100 text-purple-800 border border-purple-200"
                            title="Promoted to onboarding">
                            Onboarding
                          </span>
                        )}
                      </div>
                      {lead.tags && lead.tags.length > 0 && (
                        <div className="flex gap-1">
                          {lead.tags.map((tag, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 text-xs bg-[#E0F2FE] text-[#00B4D8] rounded-full"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm text-[#6C757D]">
                      <div>
                        <span className="font-medium">Contact:</span> {lead.contact_person}
                      </div>
                      <div>
                        <span className="font-medium">Email:</span> {lead.email}
                      </div>
                      {lead.phone && (
                        <div>
                          <span className="font-medium">Phone:</span> {lead.phone}
                        </div>
                      )}
                      {lead.source && (
                        <div>
                          <span className="font-medium">Source:</span> {lead.source}
                        </div>
                      )}
                      {lead.assigned_to && (
                        <div>
                          <span className="font-medium">Assigned To:</span> {lead.assigned_to}
                        </div>
                      )}
                    </div>
                    {lead.notes && (
                      <div className="mt-3 text-sm text-[#6C757D]">
                        <span className="font-medium">Notes:</span> {lead.notes}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    {lead.is_deleted ? (
                      <Button
                        onClick={() => handleRestore(lead.id)}
                        variant="outline"
                        size="sm"
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Restore
                      </Button>
                    ) : (
                      <>
                        <Button
                          onClick={() => setPromoteSource({
                            prefixed_id: `lead:${lead.id}`,
                            name: lead.company_name,
                            source: 'lead',
                          })}
                          variant="outline"
                          size="sm"
                          className="text-[#FF6B35] hover:bg-orange-50 border-orange-300"
                          title="Promote to Client / Supplier / Partner / Contact / Customer"
                          data-testid={`promote-lead-${lead.id}`}
                        >
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                        {!lead.onboarding_submission_id && (
                          <Button
                            onClick={() => handlePromoteToOnboarding(lead.id)}
                            variant="outline"
                            size="sm"
                            className="text-[#0F172A] hover:bg-slate-50 border-slate-300"
                            title="Promote to KYC onboarding (P8)"
                            data-testid={`promote-onboarding-${lead.id}`}
                          >
                            <FileBadge className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          onClick={() => handleEdit(lead)}
                          variant="outline"
                          size="sm"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => handleDelete(lead.id)}
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalCount > pagination.limit && (
        <div className="mt-6 flex items-center justify-center space-x-4">
          <Button
            onClick={handlePrevPage}
            disabled={pagination.skip === 0}
            variant="outline"
          >
            Previous
          </Button>
          <span className="text-sm text-[#6C757D]">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            onClick={handleNextPage}
            disabled={pagination.skip + pagination.limit >= totalCount}
            variant="outline"
          >
            Next
          </Button>
        </div>
      )}

      {/* Modals */}
      <CreateLeadModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          setIsCreateModalOpen(false);
          fetchLeads();
          fetchCount();
        }}
      />

      <PromoteCounterpartyModal
        open={!!promoteSource}
        onClose={() => setPromoteSource(null)}
        source={promoteSource}
        onPromoted={() => fetchLeads()}
      />

      {selectedLead && (
        <EditLeadModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedLead(null);
          }}
          lead={selectedLead}
          onSuccess={() => {
            setIsEditModalOpen(false);
            setSelectedLead(null);
            fetchLeads();
          }}
        />
      )}
    </div>
  );
};

export default Leads;